import { CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationStart, Router } from "@angular/router";
import {
  NgxScannerQrcodeComponent,
  ScannerQRCodeResult,
  ScannerQRCodeSymbolType,
} from "ngx-scanner-qrcode";
import { MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { filter, take } from "rxjs";

/**
 * Encapsula ngx-scanner-qrcode e garante parada da câmera ao navegar, fechar bottom sheet
 * ou destruir o componente.
 */
@Component({
  selector: "app-qr-code-scanner",
  templateUrl: "./qr-code-scanner.component.html",
  imports: [CommonModule, NgxScannerQrcodeComponent],
})
export class QrCodeScannerComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly bottomSheetRef = inject(MatBottomSheetRef, { optional: true });

  private _ngx?: NgxScannerQrcodeComponent;
  private sessionHandled = false;
  private teardown = false;
  /** Impede start pendente (getUserMedia) de religar a câmera após stop. */
  private cameraStopped = false;

  readonly qrSymbolTypes = [
    ScannerQRCodeSymbolType.ScannerQRCode_QRCODE,
    ScannerQRCodeSymbolType.ScannerQRCode_CODE39,
    ScannerQRCodeSymbolType.ScannerQRCode_CODE128,
  ];
  readonly wasmUrl = "assets/wasm/ngx-scanner-qrcode.wasm";

  @Input() isBeep = true;
  /** Se true, após o primeiro código válido a câmera para até {@link restart}. */
  @Input() stopAfterDecode = true;

  @Output() decoded = new EventEmitter<string>();

  @ViewChild(NgxScannerQrcodeComponent)
  set ngxScanner(comp: NgxScannerQrcodeComponent | undefined) {
    this._ngx = comp;
    if (comp && !this.teardown && !this.cameraStopped) {
      queueMicrotask(() => this.startIfPossible());
    }
  }

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.stopCamera());

    if (this.bottomSheetRef) {
      this.bottomSheetRef
        .backdropClick()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.stopCamera());
      this.bottomSheetRef
        .keydownEvents()
        .pipe(
          filter((e) => e.key === "Escape"),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.stopCamera());
    }
  }

  ngOnDestroy(): void {
    this.teardown = true;
    this.stopCamera();
  }

  /** Libera a câmera imediatamente (útil antes de fechar bottom sheet manualmente). */
  stopCamera(): void {
    this.cameraStopped = true;
    this.stopNgxCamera();
  }

  /** Nova tentativa de leitura após erro de validação ou API. */
  restart(): void {
    if (this.teardown) {
      return;
    }
    this.cameraStopped = false;
    this.sessionHandled = false;
    queueMicrotask(() => this.startIfPossible());
  }

  onNgxEvent(results: ScannerQRCodeResult[]) {
    if (this.teardown || (this.sessionHandled && this.stopAfterDecode)) {
      return;
    }
    const value = results?.[0]?.value?.trim();
    if (!value) {
      return;
    }
    if (this.stopAfterDecode) {
      this.sessionHandled = true;
      this.stopCamera();
    }
    this.decoded.emit(value);
  }

  private startIfPossible(): void {
    const cam = this._ngx;
    if (this.teardown || this.cameraStopped || !cam || cam.isStart) {
      return;
    }
    if (this.sessionHandled && this.stopAfterDecode) {
      return;
    }
    cam.isReady
      .pipe(take(1), filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (
          this.teardown ||
          this.cameraStopped ||
          !this._ngx ||
          this._ngx.isStart ||
          (this.sessionHandled && this.stopAfterDecode)
        ) {
          return;
        }
        this._ngx
          .start()
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              if (this.teardown || this.cameraStopped) {
                this.stopNgxCamera();
              }
            },
            error: () => {},
          });
      });
  }

  private stopNgxCamera(): void {
    try {
      this._ngx?.stop();
      const video = this._ngx?.video?.nativeElement;
      const stream = video?.srcObject;
      if (stream instanceof MediaStream) {
        stream.getTracks().forEach((track) => track.stop());
        video.srcObject = null;
      }
    } catch {
      /* ignore */
    }
  }
}
